import { deepMerge, params } from '@Visma-Real-Estate-Solutions/acquisit-ui-vue/functions'
import { useLogger } from '@Visma-Real-Estate-Solutions/acquisit-ui-vue/library'
import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios'
import { ResponseType } from '@/lib/backend-cors'
import { errorMessage } from '@/lib/ui'

export interface AxiosBackendOptions {
	name: string
	baseURL: string
	headers?: Record<string, string> | (() => Record<string, string>)
}

export type AxiosRequestData<T = any> = Record<string | number, T> | FormData | URLSearchParams | null

export const useAxios = (options: AxiosBackendOptions) => {
	const log = useLogger(options.name, useLogger.COLOR_BACKEND)
	
	// Meta Information
	const makeURL = (path: string, query: Record<string, any> = {}): string => {
		const queryParams = deepMerge(options.headers ?? {}, query)
		
		return options.baseURL + path + '?' + params(queryParams)
	}
	
	// Requests
	const request = async <T = any>(
		method: string,
		path: string,
		query: Record<string, any>,
		data: AxiosRequestData,
		headers: Record<string, any> = {},
		handleErrors: boolean = true
	): Promise<AxiosResponse<T>> => {
		const evaluatedBaseHeaders = typeof(options.headers) === 'function' ?
		                             options.headers() :
		                             (options.headers ?? {})
		
		const axiosOptions: AxiosRequestConfig = {
			method,
			baseURL: options.baseURL,
			headers: deepMerge(evaluatedBaseHeaders, headers),
			url: path,
			params: query,
			data
		}
		
		if (data instanceof FormData) {
			axiosOptions.headers!['Content-Type'] = 'multipart/form-data'
			axiosOptions.responseType = 'text' // No processing for FormData
		} else if (data instanceof URLSearchParams) {
			axiosOptions.headers!['Content-Type'] = 'application/x-www-form-urlencoded'
		} else if (data?.headers?.['Content-Type'] == 'text/plain') {
			axiosOptions.responseType = 'text'
		} else {
			axiosOptions.headers!['Content-Type'] = 'application/json; charset=utf8'
			axiosOptions.responseType = 'json'
		}
		
		const promise = axios<T>(axiosOptions)
		
		if (handleErrors) {
			promise.catch(error => {
				onError(parseError(error))
			})
		}
		
		return promise
	}
	
	/**
	 * Create an alias to the request function with the method already set
	 */
	const alias = (method: string): (<R = any>(path: string, query?: Record<string, any>, data?: AxiosRequestData, handleErrors?: boolean) => Promise<AxiosResponse<R>>) => {
		return <R = any>(path: string, query: Record<string, any> = {}, data: AxiosRequestData = null, handleErrors = true): Promise<AxiosResponse> => {
			return request<R>(method, path, query, data, {}, handleErrors)
		}
	}
	
	/**
	 * Create an alias to the request function with the method already set, but don't allow data to be passed
	 */
	const aliasNoData = (method: string): (<R = any>(path: string, query?: Record<string, any>, handleErrors?: boolean) => Promise<AxiosResponse<R>>) => {
		return (path, query = {}, handleErrors = true): Promise<AxiosResponse> => {
			return request(method, path, query, null, {}, handleErrors)
		}
	}
	
	/**
	 * Retry a request when the response data was null
	 *
	 * @param requestPromise    Function returning a promise to retry
	 * @param times             Maximum number of times to try
	 * @param timeout           Time to wait between requests in ms
	 *
	 * @return Returned promise either resolves with the same data the requestPromise returned or throws an NoResponseError or other error
	 */
	const retry = <T = AxiosResponse>(requestPromise: () => Promise<T>, times: number, timeout: number = 350): Promise<T> => {
		let go = (times: number) => {
			return requestPromise().then(response => {
				if (response === null || (<any> response).status && (<any> response).data === null) {
					throw new Error('data is null')
				}
				
				return response
			}).catch(e => {
				if (e.message == 'data is null') {
					if (times - 1 > 0) {
						return new Promise((resolve) => {
							setTimeout(_ => {
								resolve(null)
							}, timeout)
						}).then(_ => go(times - 1))
					} else {
						throw {
							type: ResponseType.NoResponseError,
							message: 'No response'
						}
					}
				}
				
				throw e
			})
		}
		
		return go(times)
	}
	
	// Error Handling
	
	const parseError = (error: any) => {
		if (typeof(error) === 'string') {
			return {
				type: ResponseType.BackendError,
				message: error
			}
		} else if (error?.response) {
			let message: string,
				code: number|null = null,
				details = null
			
			if (typeof(error.response.data) == 'string') {
				message = error.response.data
			} else if (error.response.data && error.response.data.exception) {
				message = error.response.data.exception + (error.response.data.message ? ': ' + error.response.data.message : '')
				details = error.response.data.trace
			} else if (error.response.data) {
				message = error.response.data.message || JSON.stringify(error.response.data)
				code = error.response.data.code || null
				details = error.response.data.details || null
			} else if (error.response.statusText) {
				message = error.response.statusText + ' [' + JSON.stringify(error.response.data) + ']'
				code = error.response.status
				details = null
			} else {
				message = 'Unknown error: ' + JSON.stringify(error.response)
				code = null
				details = null
			}
			
			return {
				type: ResponseType.BackendError,
				status: error.response.status,
				headers: error.response.headers,
				message,
				code,
				details,
				request: error.request
			}
		} else if (error && error.request) {
			return {
				type: ResponseType.NoResponseError,
				message: 'Response missing',
				request: error.request
			}
		} else if (error instanceof Error) {
			return {
				type: ResponseType.JSError,
				message: error.toString(),
				error
			}
		} else {
			return {
				type: ResponseType.NetworkError,
				message: error && error.message ? error.message : error
			}
		}
	}
	
	const onError = (error: any) => {
		log.always.errorFrom('onError', error)
		
		if (error.type) {
			switch (error.type) {
				case ResponseType.NoResponseError:
					const message = 'No server response'
					errorMessage(message, 'Network Error')
					
					return message
				
				case ResponseType.NetworkError:
					errorMessage(error.message, 'Network Error')
					return error.message
				
				case ResponseType.BackendError:
					if (error.status == 500) {
						try {
							let errorJSON = {
								status: error.status,
								code: error.code,
								message: error.message,
								details: error.details
							}
							
							errorMessage('<pre style="text-align:left; overflow-x: auto; -webkit-overflow-scrolling: touch;">' + JSON.stringify(errorJSON, null, 4) + '</pre>', 'Internal Error', 0, true, false, true)
							
							return error.message
						} catch (e) {
							errorMessage(error, undefined, 0, true, false, true)
							return error
						}
					} else if (error.message) {
						errorMessage(error.message)
						return error.message
					} else {
						errorMessage('<pre style="text-align:left; overflow-x: auto; -webkit-overflow-scrolling: touch;">' + JSON.stringify(error, null, 4) + '</pre>', 'Internal Error', 0, true, false, true)
						return error.message
					}
				
				case ResponseType.JSError:
					console.error(error.error)
					errorMessage(error.message)
					
					return error.message
			}
		} else {
			return onError(parseError(error))
		}
	}
	
	return {
		makeURL,
		request,
		/**
		 * Make a GET request
		 */
		get: aliasNoData('get'),
		/**
		 * Make a PATCH request
		 */
		patch: alias('patch'),
		/**
		 * Make a POST request
		 */
		post: alias('post'),
		/**
		 * Make a PUT request
		 */
		put: alias('put'),
		/**
		 * Make a DELETE request
		 */
		delete: aliasNoData('delete'),
		/**
		 * Make a HEAD request
		 */
		head: aliasNoData('head'),
		
		retry,
		parseError,
		onError,
		
		get name(): string {
			return options.name
		},
		
		get baseURL(): string {
			return options.baseURL
		}
	}
}